import { useEffect, useRef, useState } from 'react';

/**
 * useCalibrationTip — decides when to show the "calibrate your compass"
 * strip inside the Qibla card. Only symptoms of a real problem trigger it:
 *   1. no heading reading within 5 s
 *   2. heading is relative only (not tied to North)
 *   3. iOS reports compass accuracy worse than 25°
 *   4. heading swings >15° in 2 s while the gyroscope says the phone is still
 * Touch devices only, once per app session, user can switch it off.
 */
const NO_READING_MS = 5000;
const WINDOW_MS = 2000;
const JITTER_DEG = 15;
const STILL_GYRO_DEG = 5;
const LOW_ACCURACY_DEG = 25;
const SHOW_AFTER_MS = 1500;
const READY_AFTER_MS = 3000;
const READY_VISIBLE_MS = 2500;
const PREF_KEY = 'falak.qibla.calibrationTip';

let shownThisSession = false;

export const isCalibrationTipEnabled = () => {
  try { return localStorage.getItem(PREF_KEY) !== 'off'; } catch (_) { return true; }
};
export const setCalibrationTipEnabled = (on) => {
  try { localStorage.setItem(PREF_KEY, on ? 'on' : 'off'); } catch (_) { /* ignore */ }
};

const isTouchDevice = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

export function useCalibrationTip({ heading, source, accuracy, enabled, sensorReady }) {
  const [noReading, setNoReading] = useState(false);
  const [interference, setInterference] = useState(false);
  const [tip, setTip] = useState('hidden');
  const headingSamples = useRef([]);
  const gyroSamples = useRef([]);
  const watching = enabled && sensorReady && isTouchDevice();
  const hasHeading = heading != null;

  useEffect(() => {
    setNoReading(false);
    if (!watching || hasHeading) return undefined;
    const t = setTimeout(() => setNoReading(true), NO_READING_MS);
    return () => clearTimeout(t);
  }, [watching, hasHeading]);

  // Gyroscope tells real rotation apart from magnetic noise.
  useEffect(() => {
    if (!watching) return undefined;
    let last = null;
    const onMotion = (e) => {
      const r = e.rotationRate;
      if (!r || r.alpha == null) return;
      const now = performance.now();
      if (last != null) {
        const dt = (now - last) / 1000;
        const rate = Math.abs(r.alpha) + Math.abs(r.beta || 0) + Math.abs(r.gamma || 0);
        gyroSamples.current.push({ t: now, deg: rate * dt });
      }
      last = now;
    };
    window.addEventListener('devicemotion', onMotion);
    return () => window.removeEventListener('devicemotion', onMotion);
  }, [watching]);

  useEffect(() => {
    if (!watching || heading == null) return;
    const now = performance.now();
    const cutoff = now - WINDOW_MS;
    const hs = headingSamples.current.filter((s) => s.t >= cutoff);
    hs.push({ t: now, h: heading });
    headingSamples.current = hs;
    gyroSamples.current = gyroSamples.current.filter((s) => s.t >= cutoff);
    const gs = gyroSamples.current;
    if (gs.length < 10 || now - hs[0].t < WINDOW_MS * 0.75) return;
    const values = hs.map((s) => s.h);
    const swing = Math.max(...values) - Math.min(...values);
    const rotated = gs.reduce((sum, s) => sum + s.deg, 0);
    setInterference(swing > JITTER_DEG && rotated < STILL_GYRO_DEG);
  }, [watching, heading]);

  const lowAccuracy = accuracy != null && (accuracy < 0 || accuracy > LOW_ACCURACY_DEG);
  const problem = watching && (noReading || source === 'relative' || lowAccuracy || interference);

  useEffect(() => {
    let t;
    if (tip === 'hidden' && problem && !shownThisSession) {
      t = setTimeout(() => { shownThisSession = true; setTip('shown'); }, SHOW_AFTER_MS);
    } else if (tip === 'shown' && !problem) {
      t = setTimeout(() => setTip('ready'), READY_AFTER_MS);
    } else if (tip === 'ready') {
      t = setTimeout(() => setTip('hidden'), READY_VISIBLE_MS);
    }
    return () => clearTimeout(t);
  }, [tip, problem]);

  return { tip, dismiss: () => setTip('hidden') };
}
